import React, { useState } from 'react';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import { v4 as uuidv4 } from 'uuid';

import { Modal, useT } from '@repo/common';
import MobileExperienceFullPage from './MobileExperienceFullPage';

import { ReactComponent as DragAndDropIcon } from '@repo/common/assets/icons/16px/icon_16px_drag-and-drop.svg';
import { ReactComponent as DeleteIcon } from '@repo/common/assets/icons/24px/icon_24px_delete_outline.svg';
import { ReactComponent as PlusIcon } from '@repo/common/assets/icons/20px/icon_20px_plus_outline.svg';

const PLACEHOLDER_TEXT = 'Tap to add your job and company';
const PLACEHOLDER_TEXT_KR = '탭하여 직무와 회사를 추가하세요';

const EMPTY_ENTRY = {
  company: '',
  jobTitle: '',
  employmentType: '',
  employmentPeriod: '',
  location: '',
  locationType: '',
  headline: '',
  keyAchievements: [],
};

const formatTitle = (exp, t) => {
  const job = exp.jobTitle?.trim();
  const company = exp.company?.trim();
  if (job && company) {
    return t(`${job} at ${company}`, `${company} · ${job}`);
  }
  return job || company;
};

const PlaceholderCard = ({ onClick }) => {
  const t = useT();
  return (
    <div className='flex items-center w-full'>
      <span className='shrink-0 text-grayscale-gray04 py-[14px] w-[20px]'>
        <DragAndDropIcon />
      </span>
      <button
        type='button'
        onClick={onClick}
        className='flex-1 text-left rounded-[8px] border border-grayscale-gray02 bg-grayscale-gray00 p-3 typo-b14Strong text-text-placeholder'
      >
        {t(PLACEHOLDER_TEXT, PLACEHOLDER_TEXT_KR)}
      </button>
    </div>
  );
};

const MobileExperienceForm = ({ experience, setResumeData, $isMobile }) => {
  const t = useT();
  const list = Array.isArray(experience) ? experience : [];

  const [editor, setEditor] = useState({ open: false, mode: 'add', id: null });
  const [deleteTargetId, setDeleteTargetId] = useState(null);

  const commit = (next) =>
    setResumeData((prev) => ({ ...prev, experience: next }));

  const openAdd = () => setEditor({ open: true, mode: 'add', id: null });
  const openEdit = (id) => setEditor({ open: true, mode: 'edit', id });
  const closeEditor = () => setEditor({ open: false, mode: 'add', id: null });

  const addRow = () => commit([...list, { id: uuidv4(), ...EMPTY_ENTRY }]);

  const handleSave = (data) => {
    if (editor.mode === 'add') {
      commit([...list, { id: uuidv4(), ...data }]);
    } else {
      commit(list.map((e) => (e.id === editor.id ? { ...e, ...data } : e)));
    }
    closeEditor();
  };

  const handleConfirmDelete = () => {
    commit(list.filter((e) => e.id !== deleteTargetId));
    setDeleteTargetId(null);
  };

  const onDragEnd = (result) => {
    if (!result.destination) return;
    const items = Array.from(list);
    const [moved] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, moved);
    commit(items);
  };

  const editingEntry =
    editor.mode === 'edit' ? list.find((e) => e.id === editor.id) : null;

  return (
    <div className='flex flex-col gap-3 w-full'>
      {list.length === 0 ? (
        <PlaceholderCard onClick={openAdd} />
      ) : (
        <DragDropContext onDragEnd={onDragEnd}>
          <Droppable droppableId='mobile-experiences' direction='vertical'>
            {(provided) => (
              <div
                ref={provided.innerRef}
                {...provided.droppableProps}
                className='flex flex-col gap-3 w-full'
              >
                {list.map((exp, index) => {
                  const title = formatTitle(exp, t);
                  const achievementTitles = (exp.keyAchievements || [])
                    .map((k) => k.title?.trim())
                    .filter(Boolean);

                  return (
                    <Draggable
                      key={exp.id}
                      draggableId={String(exp.id)}
                      index={index}
                    >
                      {(prov) => (
                        <div
                          ref={prov.innerRef}
                          {...prov.draggableProps}
                          className='flex items-center gap-2 w-full'
                        >
                          {/* Drag handle */}
                          <span
                            {...prov.dragHandleProps}
                            className='shrink-0 cursor-grab active:cursor-grabbing text-grayscale-gray03'
                          >
                            <DragAndDropIcon width={20} height={20} />
                          </span>

                          <div className='flex-1 min-w-0 flex items-center rounded-[8px] border border-grayscale-gray02 bg-grayscale-gray00'>
                            <button
                              type='button'
                              onClick={() => openEdit(exp.id)}
                              className='flex-1 min-w-0 flex flex-col items-start gap-1 text-left px-4 py-3'
                            >
                              <p
                                className={`truncate max-w-full typo-b14Strong ${
                                  title
                                    ? 'text-text-primary'
                                    : 'text-text-placeholder'
                                }`}
                              >
                                {title ||
                                  t(PLACEHOLDER_TEXT, PLACEHOLDER_TEXT_KR)}
                              </p>
                              {exp.employmentType?.trim() && (
                                <p className='truncate max-w-full typo-b12 text-text-secondary'>
                                  {exp.employmentType}
                                </p>
                              )}
                              {exp.employmentPeriod?.trim() && (
                                <p className='truncate max-w-full typo-b12 text-text-secondary'>
                                  {exp.employmentPeriod}
                                </p>
                              )}
                              {exp.location?.trim() && (
                                <p className='truncate max-w-full typo-b12 text-text-secondary'>
                                  {exp.location}
                                </p>
                              )}
                              {achievementTitles.map((t, i) => (
                                <p
                                  key={i}
                                  className='truncate max-w-full typo-b12 text-text-primary'
                                >
                                  {t}
                                </p>
                              ))}
                            </button>

                            <button
                              type='button'
                              onClick={() => setDeleteTargetId(exp.id)}
                              className='shrink-0 self-stretch flex items-center px-3 text-grayscale-gray04'
                              aria-label={t('Delete', '삭제')}
                            >
                              <DeleteIcon width={20} height={20} />
                            </button>
                          </div>
                        </div>
                      )}
                    </Draggable>
                  );
                })}
                {provided.placeholder}
              </div>
            )}
          </Droppable>
        </DragDropContext>
      )}

      <button
        type='button'
        onClick={addRow}
        className='flex items-center justify-center h-12 w-full rounded-[8px] bg-grayscale-gray01 text-grayscale-gray05 transition-colors active:bg-grayscale-gray02'
        aria-label={t('Add experience', '경력 추가하기')}
      >
        <PlusIcon width={20} height={20} />
      </button>

      {editor.open && (
        <MobileExperienceFullPage
          initialData={editingEntry}
          onClose={closeEditor}
          onSave={handleSave}
          $isMobile={$isMobile}
        />
      )}

      {deleteTargetId && (
        <Modal
          isOpen
          onClickLeftBtn={() => setDeleteTargetId(null)}
          onClickRightBtn={handleConfirmDelete}
          title={t(
            'Delete this experience entry?',
            '이 경력 항목을 삭제할까요?',
          )}
          subText={t(
            'This action can’t be undone.',
            '이 작업은 되돌릴 수 없습니다.',
          )}
          buttonSize='xl'
          leftButtonLabel={t('cancel', '취소')}
          leftHideIcon={true}
          rightButtonLabel={t('Delete', '삭제')}
          rightHideIcon={true}
          type='warning'
        />
      )}
    </div>
  );
};

export default MobileExperienceForm;
